'use client';

import { forwardRef, useEffect, useCallback, HTMLAttributes } from 'react';
import { createPortal } from 'react-dom';
import { usePortalContainer } from '../primitives/portal-container';
import NavArrow from '../primitives/NavArrow';
import CloseButton from '../primitives/CloseButton';
import { lockBodyScroll, unlockBodyScroll } from '../utils/scrollLock';
import styles from './Lightbox.module.css';
import Icon from '../primitives/Icon';

export interface LightboxImage {
  src: string;
  alt: string;
  /** Shows a play badge over the frame — the source is a video still. */
  video?: boolean;
}

interface LightboxProps extends HTMLAttributes<HTMLDivElement> {
  images: LightboxImage[];
  /** Which image is showing, or null when the lightbox is closed (controlled). */
  index: number | null;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  /**
   * Render the portal into this element instead of `document.body`.
   * Falls back to `PortalContainerContext`, then `document.body`.
   */
  portalContainer?: Element | null;
}

const Glyph = ({ name, size = 24 }: { name: string; size?: number }) => (
  <Icon name={name} size={size} />
);

/**
 * The full-screen image viewer — backdrop, prev/next, counter, close. Controlled by
 * `index`, so any grid can own the thumbnails and hand off to one shared viewer:
 * ImageGallery drives it from its own grid, the message thread from a media bubble.
 */
const Lightbox = forwardRef<HTMLDivElement, LightboxProps>(({
  images,
  index,
  onIndexChange,
  onClose,
  portalContainer,
  className = '',
  ...props
}, ref) => {
  const portalTarget = usePortalContainer(portalContainer);
  const open = index !== null && index >= 0 && index < images.length;

  const goNext = useCallback(() => {
    if (index === null) return;
    onIndexChange((index + 1) % images.length);
  }, [index, images.length, onIndexChange]);

  const goPrev = useCallback(() => {
    if (index === null) return;
    onIndexChange((index - 1 + images.length) % images.length);
  }, [index, images.length, onIndexChange]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'ArrowLeft') goPrev();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, goNext, goPrev, onClose]);

  useEffect(() => {
    if (!open) return;
    lockBodyScroll();
    return () => unlockBodyScroll();
  }, [open]);

  if (!open || !portalTarget) return null;

  return createPortal(
    <div
      ref={ref}
      className={`${styles.lightbox} ${className}`}
      role="dialog"
      aria-modal="true"
      aria-label="Media preview"
      {...props}
    >
      <div className={styles.backdrop} onClick={onClose} aria-hidden="true" />

      <div className={styles.header}>
        <span className={styles.counter}>{index! + 1} / {images.length}</span>
        <CloseButton className={styles.button} onClick={onClose} aria-label="Close preview" size="lg" />
      </div>

      <div className={styles.content}>
        {images.length > 1 && (
          <NavArrow
            direction="prev"
            className={`${styles.nav} ${styles.navLeft}`}
            onClick={goPrev}
            aria-label="Previous"
          />
        )}

        <div className={styles.frame}>
          {/* dynamic index (D9 class 3, DRIVEN): `open` (above) requires index in
              [0, images.length), and the early return on !open guards this whole render;
              observed by tests/visual/dynamic-index-guard.spec.ts opening the Lightbox on a
              showcase image. */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={images[index!]!.src} alt={images[index!]!.alt} className={styles.image} />
          {images[index!]!.video && (
            <span className={styles.play} aria-hidden="true"><Glyph name="play_arrow" size={40} /></span>
          )}
        </div>

        {images.length > 1 && (
          <NavArrow
            direction="next"
            className={`${styles.nav} ${styles.navRight}`}
            onClick={goNext}
            aria-label="Next"
          />
        )}
      </div>
    </div>,
    portalTarget,
  );
});

Lightbox.displayName = 'Lightbox';
export default Lightbox;
